import React, { useState, useRef, useEffect } from 'react';
import { EmailTemplate, BrandStyleGuide } from '../../../types';
import { ArrowLeft, Eye, Code, Monitor, Smartphone, Variable } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import { defaultBrandStyle, wrapInBaseTemplate } from '../../../utils/emailTemplateBuilder';

interface TemplateEditorProps {
  template: EmailTemplate | null;
  onSave: (data: Partial<EmailTemplate>) => Promise<void>;
  onCancel: () => void;
}

const AVAILABLE_VARIABLES = [
  { key: 'email', label: 'Email Address', example: 'user@example.com' },
  { key: 'firstName', label: 'First Name', example: 'John' },
  { key: 'lastName', label: 'Last Name', example: 'Doe' },
  { key: 'unsubscribeUrl', label: 'Unsubscribe URL', example: '#unsubscribe' },
  { key: 'currentYear', label: 'Current Year', example: new Date().getFullYear().toString() },
  { key: 'companyName', label: 'Company Name', example: '4HVNSK8' },
];

type EditorTab = 'edit' | 'preview';
type PreviewDevice = 'desktop' | 'mobile';

const TemplateEditor: React.FC<TemplateEditorProps> = ({ template, onSave, onCancel }) => {
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [previewText, setPreviewText] = useState(template?.previewText || '');
  const [category, setCategory] = useState<EmailTemplate['category']>(template?.category || 'newsletter');
  const [htmlContent, setHtmlContent] = useState(template?.htmlContent || '');
  const [activeTab, setActiveTab] = useState<EditorTab>('edit');
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');
  const [saving, setSaving] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [brandStyle, setBrandStyle] = useState<BrandStyleGuide>(defaultBrandStyle);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const init = async () => {
        // Fetch brand style from settings
        try {
            const docRef = doc(db, 'settings', 'email_branding');
            const snap = await getDoc(docRef);
            let currentStyle = defaultBrandStyle;
            if (snap.exists()) {
                currentStyle = { ...defaultBrandStyle, ...snap.data() } as BrandStyleGuide;
                setBrandStyle(currentStyle);
            }

            // Only generate default content if it's a NEW template (no template prop) AND htmlContent is empty
            if (!template && !htmlContent) {
                const s = currentStyle;
                const defaultBody = `
              <h2 style="color: ${s.colors.primary}; margin: 0 0 20px 0; font-size: ${s.typography.headingSize}px;">Your headline here</h2>
              <p style="color: ${s.colors.bodyText}; font-size: ${s.typography.bodySize}px; line-height: ${s.typography.lineHeight}; margin: 0 0 20px 0;">
                Your email content goes here. You can use variables like {{firstName}} to personalize your emails.
              </p>
              <div style="text-align: center; margin: 30px 0;">
                <a href="#" style="display: inline-block; background-color: ${s.colors.buttonBg}; color: ${s.colors.buttonText}; padding: 15px 40px; text-decoration: none; font-size: 14px; letter-spacing: ${s.typography.letterSpacing}; text-transform: uppercase;">
                  Call to Action
                </a>
              </div>`;
                
                const wrapped = wrapInBaseTemplate({
                    body: defaultBody,
                    includeSocial: true,
                    includeUnsubscribe: true,
                    unsubscribeUrl: '{{unsubscribeUrl}}',
                    style: currentStyle
                });
                setHtmlContent(wrapped);
            }
        } catch (error) {
            console.error("Error fetching brand style:", error);
        }
    };

    init();
  }, [template]); // Run once on mount (or if template changes)

  const handleInsertVariable = (variable: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = htmlContent;
    const before = text.substring(0, start);
    const after = text.substring(end);
    const newText = `${before}{{${variable}}}${after}`;

    setHtmlContent(newText);
    setShowVariables(false);

    // Restore focus and cursor position
    setTimeout(() => {
      textarea.focus();
      const newPos = start + variable.length + 4;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const renderPreview = () => {
    // Replace variables with example values
    let rendered = htmlContent;
    AVAILABLE_VARIABLES.forEach(v => {
      rendered = rendered.replace(new RegExp(`\\{\\{${v.key}\\}\\}`, 'g'), v.example);
    });
    return rendered;
  };

  const extractVariables = (): string[] => {
    const matches = htmlContent.match(/\{\{(\w+)\}\}/g);
    if (!matches) return [];
    return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Please enter a template name');
      return;
    }
    if (!subject.trim()) {
      alert('Please enter an email subject');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        subject: subject.trim(),
        previewText: previewText.trim(),
        category,
        htmlContent,
        variables: extractVariables()
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onCancel}
            className="flex items-center gap-2 text-gray-600 hover:text-black transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-bold uppercase tracking-wider">Back</span>
          </button>
          <h2 className="text-xl font-bold">
            {template ? 'Edit Template' : 'New Template'}
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-bold uppercase tracking-wider border border-gray-300 hover:border-black transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>

      {/* Template Info */}
      <div className="bg-white border border-gray-200 p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
              Template Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Welcome Email"
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as EmailTemplate['category'])}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black text-sm bg-white"
            >
              <option value="welcome">Welcome</option>
              <option value="newsletter">Newsletter</option>
              <option value="promotional">Promotional</option>
              <option value="transactional">Transactional</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
            Email Subject *
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g., Welcome to the crew!"
            className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">You can use variables like {'{{firstName}}'}</p>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
            Preview Text (optional)
          </label>
          <input
            type="text"
            value={previewText}
            onChange={(e) => setPreviewText(e.target.value)}
            placeholder="Text shown in email client preview..."
            className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black text-sm"
          />
        </div>
      </div>

      {/* Editor Tabs */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-200 px-4">
          <div className="flex">
            <button
              onClick={() => setActiveTab('edit')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${
                activeTab === 'edit'
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-400 hover:text-black'
              }`}
            >
              <Code size={16} />
              HTML
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${
                activeTab === 'preview'
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-400 hover:text-black'
              }`}
            >
              <Eye size={16} />
              Preview
            </button>
          </div>

          {activeTab === 'edit' && (
            <div className="relative">
              <button
                onClick={() => setShowVariables(!showVariables)}
                className="flex items-center gap-2 px-3 py-1 text-xs font-bold uppercase tracking-wider border border-gray-300 hover:border-black transition-colors"
              >
                <Variable size={14} />
                Insert Variable
              </button>
              {showVariables && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 shadow-lg rounded-sm z-10 min-w-[200px]">
                  {AVAILABLE_VARIABLES.map(v => (
                    <button
                      key={v.key}
                      onClick={() => handleInsertVariable(v.key)}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <div className="font-bold">{`{{${v.key}}}`}</div>
                      <div className="text-xs text-gray-500">{v.label}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="flex gap-1">
              <button
                onClick={() => setPreviewDevice('desktop')}
                className={`p-2 rounded transition-colors ${
                  previewDevice === 'desktop' ? 'bg-gray-200' : 'hover:bg-gray-100'
                }`}
                title="Desktop preview"
              >
                <Monitor size={16} />
              </button>
              <button
                onClick={() => setPreviewDevice('mobile')}
                className={`p-2 rounded transition-colors ${
                  previewDevice === 'mobile' ? 'bg-gray-200' : 'hover:bg-gray-100'
                }`}
                title="Mobile preview"
              >
                <Smartphone size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="min-h-[500px]">
          {activeTab === 'edit' ? (
            <textarea
              ref={textareaRef}
              value={htmlContent}
              onChange={(e) => setHtmlContent(e.target.value)}
              className="w-full h-[500px] p-4 font-mono text-sm resize-none focus:outline-none"
              spellCheck={false}
            />
          ) : (
            <div className="bg-gray-100 p-4 min-h-[500px] flex justify-center">
              <div
                className={`bg-white shadow-lg transition-all duration-300 ${
                  previewDevice === 'mobile' ? 'w-[375px]' : 'w-full max-w-[700px]'
                }`}
              >
                <iframe
                  srcDoc={renderPreview()}
                  className="w-full h-[500px] border-0"
                  title="Email Preview"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Variables Used */}
      <div className="bg-white border border-gray-200 p-4">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
          Variables in Template
        </h3>
        <div className="flex flex-wrap gap-2">
          {extractVariables().length > 0 ? (
            extractVariables().map(v => (
              <span
                key={v}
                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded"
              >
                {`{{${v}}}`}
              </span>
            ))
          ) : (
            <span className="text-sm text-gray-400">No variables used</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
